/**
 * The user's connections, keyed by id.
 *
 * Every module built on top of this platform reads from here rather than
 * fetching its own list, so there is one cache to invalidate after a connect or
 * a revoke and no chance of two views disagreeing about what is connected.
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { connectionsApi } from '@/core/api/connections.api'
import { useAuth } from '@/core/auth/auth.context'
import { invalidateConnections, queryKeys } from '@/lib/query.client'
import type { Connection } from '@/core/types/api.types'

interface ConnectionContextValue {
  connections: Connection[]
  byId: Record<string, Connection>
  /** Every connection for one connector, e.g. three separate GitHub accounts. */
  byConnector: Record<string, Connection[]>
  isLoading: boolean
  error: unknown
  refresh: () => Promise<unknown>
  get: (connectionId: string) => Connection | undefined
}

const ConnectionContext = createContext<ConnectionContextValue | null>(null)

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const { user, loginRequired, initialising } = useAuth()

  const query = useQuery({
    queryKey: queryKeys.connections(),
    queryFn: () => connectionsApi.list(),
    // Never fetch before we know who is calling: doing so 401s on every page
    // load and leaves a failed query cached under the wrong identity.
    enabled: !initialising && (Boolean(user) || !loginRequired),
  })

  const connections = query.data ?? []

  const { byId, byConnector } = useMemo(() => {
    const ids: Record<string, Connection> = {}
    const connectors: Record<string, Connection[]> = {}
    for (const conn of connections) {
      ids[conn.connection_id] = conn
      ;(connectors[conn.connector_id] ??= []).push(conn)
    }
    return { byId: ids, byConnector: connectors }
  }, [connections])

  const refresh = useCallback(() => invalidateConnections(), [])
  const get = useCallback((id: string) => byId[id], [byId])

  const value = useMemo<ConnectionContextValue>(
    () => ({
      connections,
      byId,
      byConnector,
      isLoading: query.isLoading,
      error: query.error,
      refresh,
      get,
    }),
    [connections, byId, byConnector, query.isLoading, query.error, refresh, get],
  )

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>
}

export function useConnections(): ConnectionContextValue {
  const ctx = useContext(ConnectionContext)
  if (!ctx) throw new Error('useConnections must be used inside <ConnectionProvider>')
  return ctx
}
